import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { onAuthStateChanged } from "firebase/auth";
import { auth, methodologyService } from "../../lib/firebase";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { ToggleSwitch } from "../../components/ui/toggle-switch";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { ExternalLink, Settings, Users, Database, Shield } from "lucide-react";
import { toast } from "../../components/ui/use-toast";

const ADMIN_EMAIL = "admin@rawpa.com";

export const AdminLOLBASPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lolbasEnabled, setLolbasEnabled] = useState(true);
  const [apiStatus, setApiStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [entryCount, setEntryCount] = useState(0);
  const [customDescription, setCustomDescription] = useState("Access Living Off The Land Binaries and Scripts for Windows security testing");
  const [saving, setSaving] = useState(false);

  // Check authentication
  useEffect(() => {
    if (!auth) {
      setError("Authentication service unavailable");
      setLoading(false);
      return () => {};
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
        return;
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load LOLBAS settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await methodologyService.getLOLBASSettings();
        setLolbasEnabled(settings.enabled);
        setCustomDescription(settings.description);
      } catch (error) {
        console.error("Failed to load LOLBAS settings:", error);
        toast({
          title: "Error",
          description: "Failed to load LOLBAS settings",
          variant: "destructive",
        });
      }
    };

    loadSettings();
  }, []);

  // Check LOLBAS API status
  useEffect(() => {
    const checkApiStatus = async () => {
      try {
        setApiStatus('loading');
        const result = await methodologyService.testLOLBASAPI();
        if (result.success) {
          setEntryCount(result.entryCount || 0);
          setApiStatus('success');
        } else {
          setApiStatus('error');
        }
      } catch (error) {
        console.error('Error checking LOLBAS API:', error);
        setApiStatus('error');
      }
    };

    checkApiStatus();
  }, []);

  const handleSignOut = async () => {
    try {
      if (auth) {
        await auth.signOut();
        navigate('/signin');
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const success = await methodologyService.updateLOLBASSettings({
        enabled: lolbasEnabled,
        description: customDescription
      });

      if (success) {
        toast({
          title: "Settings Saved",
          description: "LOLBAS integration settings have been updated.",
        });
      } else {
        throw new Error("Failed to save settings");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save settings.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestApi = async () => {
    try {
      setApiStatus('loading');
      const result = await methodologyService.testLOLBASAPI();
      if (result.success) {
        setEntryCount(result.entryCount || 0);
        setApiStatus('success');
        toast({
          title: "API Test Successful",
          description: `Successfully connected to LOLBAS API. ${result.entryCount} entries available.`,
        });
      } else {
        setApiStatus('error');
        toast({
          title: "API Test Failed",
          description: `Could not connect to LOLBAS API: ${result.error}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      setApiStatus('error');
      toast({
        title: "API Test Failed",
        description: "Network error when testing LOLBAS API.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/aAyuiOcvMq/toolkit')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/aAyuiOcvMq/toolkit')}
                className="font-['Space_Grotesk'] text-black"
              >
                Back to Toolkits
              </Button>
              <div 
                onClick={handleSignOut}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8">
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              LOLBAS Integration Management
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
              Manage LOLBAS integration settings for user dashboard access
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Integration Settings */}
            <Card className="border-2 border-[#E7E7E7]">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#daed6e] rounded-lg flex items-center justify-center">
                    <Settings className="h-6 w-6 text-black" />
                  </div>
                  <div>
                    <CardTitle className="text-xl text-black">Integration Settings</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">Control LOLBAS availability for users</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Enable LOLBAS for Users</Label>
                    <p className="text-xs text-gray-500">Allow users to access LOLBAS data</p>
                  </div>
                  <ToggleSwitch
                    checked={lolbasEnabled}
                    onCheckedChange={setLolbasEnabled}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Custom Description</Label>
                  <Textarea
                    value={customDescription}
                    onChange={(e) => setCustomDescription(e.target.value)}
                    placeholder="Description shown to users"
                    className="text-sm"
                    rows={3}
                  />
                  <p className="text-xs text-gray-500">
                    This description will appear on the user toolkit page
                  </p>
                </div>

                <Button 
                  className="w-full bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                  onClick={handleSaveSettings}
                  disabled={saving}
                >
                  Save Settings
                </Button>
              </CardContent>
            </Card>

            {/* API Status */}
            <Card className="border-2 border-[#E7E7E7]">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#daed6e] rounded-lg flex items-center justify-center">
                    <Database className="h-6 w-6 text-black" />
                  </div>
                  <div>
                    <CardTitle className="text-xl text-black">API Status</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">LOLBAS API connection status</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">API Status:</span>
                  <Badge 
                    variant={apiStatus === 'success' ? 'default' : apiStatus === 'loading' ? 'secondary' : 'destructive'}
                  >
                    {apiStatus === 'success' ? 'Connected' : apiStatus === 'loading' ? 'Checking...' : 'Error'}
                  </Badge>
                </div>

                {apiStatus === 'success' && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Available Entries:</span>
                    <Badge variant="outline">{entryCount.toLocaleString()}</Badge>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">API Endpoint:</span>
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                    /api/lolbas-proxy
                  </code>
                </div>

                <Button 
                  variant="outline"
                  onClick={handleTestApi}
                  disabled={apiStatus === 'loading'}
                  className="w-full"
                >
                  Test API Connection
                </Button>

                <Button 
                  variant="outline"
                  onClick={() => window.open('https://lolbas-project.github.io/api/', '_blank')}
                  className="w-full"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View API Documentation
                </Button>
              </CardContent>
            </Card>

            {/* User Access Preview */}
            <Card className="border-2 border-[#E7E7E7]">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#daed6e] rounded-lg flex items-center justify-center">
                    <Users className="h-6 w-6 text-black" />
                  </div>
                  <div>
                    <CardTitle className="text-xl text-black">User Access Preview</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">How LOLBAS appears to users</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className={`p-4 rounded-lg border ${lolbasEnabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-[#daed6e] rounded-lg flex items-center justify-center">
                      <Shield className="h-4 w-4 text-black" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-black">LOLBAS</h4>
                      <p className="text-xs text-gray-600">Living Off The Land Binaries</p>
                    </div>
                    <Badge variant={lolbasEnabled ? 'default' : 'secondary'}>
                      {lolbasEnabled ? 'Available' : 'Disabled'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">{customDescription}</p>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="border-2 border-[#E7E7E7]">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#daed6e] rounded-lg flex items-center justify-center">
                    <ExternalLink className="h-6 w-6 text-black" />
                  </div>
                  <div>
                    <CardTitle className="text-xl text-black">Quick Actions</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">Manage LOLBAS integration</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline"
                  onClick={() => navigate('/toolkits')}
                  className="w-full justify-start"
                >
                  <Users className="w-4 h-4 mr-2" />
                  View User Toolkit Page
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => window.open('https://lolbas-project.github.io/', '_blank')}
                  className="w-full justify-start"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Visit LOLBAS Project
                </Button>

                <Button 
                  variant="outline"
                  onClick={() => window.open('https://github.com/LOLBAS-Project/LOLBAS', '_blank')}
                  className="w-full justify-start"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View GitHub Repository
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}; 